/* Y1 · the design system's shared pieces. The tokens they are built from (R,
 * TONES, T, ELEVATION, the buttons, `surface`, `chip`) live in theme.js; this
 * file exports components only, so fast refresh keeps working on it (the same
 * split as Sheet.jsx / sheetStyles.js).
 *
 * Every screen used to draw these for itself: a page title, a section title,
 * the small uppercase caption over a card, a progress bar, the × that closes
 * something, and a banner, which had four looks on the Learn home alone. */
import { C, ELEVATION, GHOST, R, T, TONES } from "./theme.js";

/* The small uppercase caption over a card or a group ("TODAY", "RANK"). */
export function Label({ children, tone = null, style = null }) {
  return <div style={{ ...T.label, color: tone ? TONES[tone].ink : C.sub, ...style }}>{children}</div>;
}

/* A page's title, with an optional way back above it and a sentence below. */
export function PageHeader({ title, subtitle = null, back = null, testId = null }) {
  return (
    <header data-testid={testId} style={{ marginBottom: 18 }}>
      {back ? (
        <button type="button" onClick={back.onClick} style={{ ...GHOST, color: C.sub, fontSize: 14, marginBottom: 4, marginLeft: -4 }}>
          {back.label}
        </button>
      ) : null}
      <h1 style={{ ...T.display, color: C.ink, margin: 0 }}>{title}</h1>
      {subtitle ? <p style={{ ...T.small, color: C.sub, margin: "6px 0 0" }}>{subtitle}</p> : null}
    </header>
  );
}

/* A section's title inside a page ("Your courses", "All courses"). */
export function SectionTitle({ children, action = null }) {
  return (
    <div style={{ alignItems: "baseline", display: "flex", gap: 12, justifyContent: "space-between", margin: "8px 0 12px" }}>
      <h2 style={{ ...T.title, color: C.ink, margin: 0 }}>{children}</h2>
      {action}
    </div>
  );
}

/* A bar for how far along something is; `value` runs 0..1. */
export function ProgressBar({ value, tone = "accent", height = 8, style = null }) {
  const pct = Math.round(Math.max(0, Math.min(1, value || 0)) * 100);
  return (
    <div style={{ background: C.faint, borderRadius: R.pill, height, overflow: "hidden", ...style }}>
      <div style={{ background: TONES[tone].solid, borderRadius: R.pill, height: "100%", transition: "width 0.4s ease", width: `${pct}%` }} />
    </div>
  );
}

/* The × that puts something away: a 32px round target, never a bare glyph. */
export function DismissButton({ label, onClick, testId = null }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      onClick={onClick}
      style={{
        alignItems: "center",
        background: "none",
        border: "none",
        borderRadius: R.pill,
        color: C.sub,
        cursor: "pointer",
        display: "flex",
        flexShrink: 0,
        fontSize: 22,
        height: 32,
        justifyContent: "center",
        lineHeight: 1,
        margin: "-4px -4px 0 0",
        width: 32,
      }}
    >
      ×
    </button>
  );
}

/* The way out of a full-screen session (a drill, Rush, the Daily Opening, the
 * Swipe review). Those four each drew their own × — a bare glyph, a grey circle,
 * a bordered square — in the same corner. One shape now: a round, bordered
 * 34px target that reads as a control on a board or on a hero. */
export function CloseButton({ label, onClick, testId = null, style = null }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      onClick={onClick}
      style={{
        alignItems: "center",
        background: C.card,
        border: `1px solid ${C.edge}`,
        borderRadius: R.pill,
        boxShadow: ELEVATION.card,
        color: C.sub,
        cursor: "pointer",
        display: "inline-flex",
        flexShrink: 0,
        fontSize: 20,
        height: 34,
        justifyContent: "center",
        lineHeight: 1,
        padding: 0,
        width: 34,
        ...style,
      }}
    >
      ×
    </button>
  );
}

/* The one banner: a moment or a message on a tone's ground — an optional icon,
 * a title (which can be the thing you tap), a sentence, an action, and an ×.
 * The Learn home's notices, "your prep held", "you closed a leak" and the
 * first-time notices all draw with it. */
export function Banner({
  tone = "neutral",
  icon = null,
  title = null,
  children = null,
  onOpen = null,
  actionLabel = null,
  onAction = null,
  onDismiss = null,
  dismissLabel = null,
  dismissTestId = null,
  testId = null,
  className = null,
}) {
  const colors = TONES[tone];
  const titleStyle = { ...T.heading, color: C.ink };
  return (
    <div
      data-testid={testId}
      className={className}
      style={{
        alignItems: "flex-start",
        background: colors.ground,
        border: `1px solid ${colors.edge}`,
        borderRadius: R.lg,
        boxShadow: ELEVATION.card,
        display: "flex",
        gap: 12,
        marginBottom: 14,
        padding: "12px 12px 12px 14px",
      }}
    >
      {icon ? (
        <span
          aria-hidden="true"
          style={{
            alignItems: "center",
            background: C.card,
            border: `1px solid ${colors.edge}`,
            borderRadius: R.md,
            display: "flex",
            flexShrink: 0,
            fontSize: 20,
            height: 40,
            justifyContent: "center",
            width: 40,
          }}
        >
          {icon}
        </span>
      ) : null}
      <div style={{ flex: 1, minWidth: 0, paddingTop: icon ? 1 : 0 }}>
        {title ? (
          onOpen ? (
            <button type="button" onClick={onOpen} style={{ ...titleStyle, background: "none", border: "none", cursor: "pointer", display: "block", padding: 0, textAlign: "left", width: "100%" }}>
              {title}
            </button>
          ) : (
            <div style={titleStyle}>{title}</div>
          )
        ) : null}
        {children ? <div style={{ ...T.small, color: title ? C.sub : C.ink, marginTop: title ? 2 : 0 }}>{children}</div> : null}
        {actionLabel && onAction ? (
          <button type="button" onClick={onAction} style={{ ...GHOST, color: colors.ink, marginLeft: -4, marginTop: 2, minHeight: 34 }}>
            {actionLabel}
          </button>
        ) : null}
      </div>
      {onDismiss ? <DismissButton label={dismissLabel} onClick={onDismiss} testId={dismissTestId} /> : null}
    </div>
  );
}
